import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Collapse,
  Flex,
  Input,
  Segmented,
  Space,
  Statistic,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';

// Унифицированная строка товара для склада
type InventoryRow = {
  id: string;
  category: 'coils' | 'plugs' | 'wires' | 'sensors' | 'clocks';
  brand: string;
  partNumber: string;
  model?: string;                 // опционально
  quantity: number;
  price: number;
  original: 'original' | 'copy';
  source: Record<string, unknown>; // исходный объект
};

// Данные прокидываем пропсами, чтобы не зависеть от конкретных хуков
type WarehouseProps = {
  coils?: any[];
  plugs?: any[];
  wires?: any[];
  sensors?: any[];
  clocks?: any[];
};

const money = (v: number) => new Intl.NumberFormat('ru-RU').format(v) + ' AMD';

const Warehouse: React.FC<WarehouseProps> = ({
  coils = [],
  plugs = [],
  wires = [],
  sensors = [],
  clocks = [],
}) => {
  // быстрый поиск / фильтр по типу
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'original' | 'copy'>('all');

  // унификация строк склада из разных коллекций
  const mapRows = useMemo(() => {
    const norm = (arr: any[], cat: InventoryRow['category']): InventoryRow[] =>
      (arr ?? []).map((it) => ({
        id: String(it.id ?? it.key ?? it.partNumber),
        category: cat,
        brand: String(it.brand ?? ''),
        partNumber: String(it.partNumber ?? ''),
        ...(it?.model ? { model: String(it.model) } : {}), // ключ model добавляем только если есть
        quantity: Number(it.quantity ?? 0),
        price: Number(it.price ?? 0),
        original: (it.original === 'original' ? 'original' : 'copy') as InventoryRow['original'],
        source: it,
      }));

    return {
      coils: norm(coils, 'coils'),
      plugs: norm(plugs, 'plugs'),
      wires: norm(wires, 'wires'),
      sensors: norm(sensors, 'sensors'),
      clocks: norm(clocks, 'clocks'),
    };
  }, [coils, plugs, wires, sensors, clocks]);

  const allRows: InventoryRow[] = useMemo(
    () => [...mapRows.coils, ...mapRows.plugs, ...mapRows.wires, ...mapRows.sensors, ...mapRows.clocks],
    [mapRows]
  );

  // фильтры
  const applyFilters = (rows: InventoryRow[]) =>
    rows.filter((r) => {
      if (typeFilter !== 'all' && r.original !== typeFilter) return false;
      if (!q) return true;
      const s = `${r.brand} ${r.partNumber} ${r.model ?? ''}`.toLowerCase();
      return s.includes(q.toLowerCase());
    });

  // единые колонки
  const columns: ColumnsType<InventoryRow> = [
    { title: 'Бренд', dataIndex: 'brand', key: 'brand', sorter: (a, b) => a.brand.localeCompare(b.brand) },
    { title: 'Номер детали', dataIndex: 'partNumber', key: 'partNumber', sorter: (a, b) => a.partNumber.localeCompare(b.partNumber) },
    {
      title: 'Модель',
      dataIndex: 'model',
      key: 'model',
      render: (v?: string) => v ?? '—',
      sorter: (a, b) => (a.model ?? '').localeCompare(b.model ?? ''),
    },
    { title: 'Кол-во', dataIndex: 'quantity', key: 'quantity', width: 110, sorter: (a, b) => a.quantity - b.quantity },
    { title: 'Тип товара', dataIndex: 'original', key: 'original', width: 120, render: (v) => (v === 'original' ? 'Оригинал' : 'Копия') },
    { title: 'Цена', dataIndex: 'price', key: 'price', align: 'right', width: 120, render: (v: number) => money(v), sorter: (a, b) => a.price - b.price },
  ];

  // панель категории
  const renderPanel = (title: string, rows: InventoryRow[]) => {
    const filtered = applyFilters(rows);
    const totalUnits = filtered.reduce((s, r) => s + r.quantity, 0);
    const totalAmount = filtered.reduce((s, r) => s + r.quantity * r.price, 0);

    return (
      <Card variant="borderless" style={{ marginTop: 8 }}>
        <Flex align="center" justify="space-between" style={{ marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
          <Space size={12}>
            <Badge color="blue" text={<span>Показано: <b>{filtered.length}</b></span>} />
            <Badge color="geekblue" text={<span>Единиц: <b>{totalUnits}</b></span>} />
            <Badge color="purple" text={<span>Сумма: <b>{money(totalAmount)}</b></span>} />
          </Space>

          {/* Быстрый выбор размера страницы (запоминаем на панель) */}
          <Segmented
            size="small"
            options={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
            defaultValue={Number(localStorage.getItem(`inv.${title}.ps`) || 10)}
            onChange={(ps) => localStorage.setItem(`inv.${title}.ps`, String(ps))}
          />
        </Flex>

        <Table<InventoryRow>
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={filtered}
          pagination={{
            defaultCurrent: 1,
            defaultPageSize: Number(localStorage.getItem(`inv.${title}.ps`) || 10),
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            position: ['bottomCenter'],
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    );
  };

  // Итоги по всем
  const allFiltered = applyFilters(allRows);
  const totalPositions = allFiltered.length;
  const totalUnits = allFiltered.reduce((s, r) => s + r.quantity, 0);
  const totalAmount = allFiltered.reduce((s, r) => s + r.quantity * r.price, 0);

  return (
    <div style={{ padding: 12 }}>
      <Flex align="center" justify="space-between" style={{ marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Склад</Typography.Title>
        <Space>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Поиск: бренд / номер / модель"
            style={{ width: 320 }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Segmented
            value={typeFilter}
            onChange={(v) => setTypeFilter(v as any)}
            options={[
              { label: 'Все', value: 'all' },
              { label: 'Оригинал', value: 'original' },
              { label: 'Копия', value: 'copy' },
            ]}
          />
          <Button icon={<DownloadOutlined />}>Экспорт в Excel</Button>
        </Space>
      </Flex>

      <Card style={{ marginBottom: 12 }}>
        <Space size={24} wrap>
          <Statistic title="Всего позиций" value={totalPositions} />
          <Statistic title="Всего единиц" value={totalUnits} />
          <Statistic title="Общая стоимость" value={money(totalAmount)} />
        </Space>
      </Card>

      <Collapse
        bordered
        defaultActiveKey={['all']}
        items={[
          { key: 'all',    label: <b>Все товары</b>,          children: renderPanel('all',    allRows) },
          { key: 'coils',  label: 'Катушки зажигания',        children: renderPanel('coils',  mapRows.coils) },
          { key: 'plugs',  label: 'Свечи зажигания',          children: renderPanel('plugs',  mapRows.plugs) },
          { key: 'wires',  label: 'Наконечники и провода',    children: renderPanel('wires',  mapRows.wires) },
          { key: 'sensors',label: 'Датчики',                  children: renderPanel('sensors',mapRows.sensors) },
          { key: 'clocks', label: 'Рулевые шлейфы',           children: renderPanel('clocks', mapRows.clocks) },
        ]}
      />
    </div>
  );
};

export default Warehouse;
